import { hasIntersectionObserverAPI } from '@bob/library'
import clsx from 'clsx'
import { styles } from 'leds'
import React, { ComponentPropsWithoutRef, FC, useEffect, useRef } from 'react'
import { match } from 'ts-pattern'

function isImage(el: HTMLImageElement | HTMLDivElement): el is HTMLImageElement {
  return el.tagName === 'IMG'
}

function setImageOnElement(
  el: HTMLImageElement | HTMLDivElement,
  src: string,
  srcSet: string
): void {
  if (isImage(el)) {
    el.src = src
    el.srcset = srcSet
  } else {
    el.style.backgroundImage = src ? `url(${src})` : ''
  }

  el.style.opacity = '1'
}

let lazyImageObserver: IntersectionObserver

if (hasIntersectionObserverAPI) {
  lazyImageObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) {
        return
      }

      const lazyEl = e.target as HTMLImageElement | HTMLDivElement
      const lazySrc = lazyEl.dataset.src ?? ''
      const lazySrcSet = lazyEl.dataset.srcset ?? ''

      setImageOnElement(lazyEl, lazySrc, lazySrcSet)

      lazyImageObserver.unobserve(lazyEl)
    })
  })
}

export interface IImageProps extends ComponentPropsWithoutRef<'img'> {
  lazy?: boolean
  objectFit?: 'cover' | 'contain'
}

export const Image: FC<IImageProps> = (props) => {
  const { className = '', style = {}, lazy = true, src, srcSet, objectFit, ...imageProps } = props

  const height = match(objectFit)
    .with('cover', () => {
      return '100%'
    })
    .with('contain', () => {
      return '100%'
    })
    .with(undefined, () => {
      return 'auto'
    })
    .exhaustive()

  const elementRef = useRef<any>()

  const { current: initialSrc } = useRef(!lazy ? src : undefined)
  const { current: initialSrcSet } = useRef(!lazy ? srcSet : undefined)

  useEffect(() => {
    const el = elementRef.current
    const instantSrc = !lazy || !hasIntersectionObserverAPI

    if (lazy && el && lazyImageObserver) {
      lazyImageObserver.observe(el)

      return () => lazyImageObserver.unobserve(el)
    } else if (instantSrc) {
      setImageOnElement(el, src ?? '', srcSet ?? '')
    }
  }, [lazy, src, srcSet])

  useEffect(() => {
    const el = elementRef.current

    if (el === null) {
      return
    }

    const imageHasBeenSet =
      (el as HTMLImageElement).src ||
      (el as HTMLImageElement).srcset ||
      (el as HTMLDivElement).style.backgroundImage

    if (imageHasBeenSet) {
      setImageOnElement(el, src ?? '', srcSet ?? '')
    }
  }, [src, srcSet])

  return (
    <img
      className={clsx(
        styles({ opacity: lazy ? 'none' : 'full', display: 'block', objectFit }),
        className
      )}
      data-src={src}
      data-srcset={srcSet}
      height={height}
      ref={elementRef}
      src={initialSrc}
      srcSet={initialSrcSet}
      style={style}
      {...imageProps}
    />
  )
}
